/**
 * useDownloadState - Hook for managing company download state
 *
 * Extracted from CompanyDetails.jsx to reduce file size and improve maintainability.
 * Manages all state and actions related to downloading company exports:
 * - Loading states for each download type (onepager, financials, full profile, comparables)
 * - Success states for each download type
 * - Computed download progress (done/maxDownloads/allDone)
 * - Download action handler
 *
 * @module useDownloadState
 */

import { useLocalObservable } from 'mobx-react';
import { downloadProfilerFile, DOWNLOAD_CONFIGS, buildDownloadUrl } from '../utils/downloadUtils';
import { HEADER_MESSAGE_TYPES } from '../../../../utils/constants/header';
import { DOWNLOAD_LIMIT_EXCEEDED_CODE } from '../../../../utils/constants/downloadLimits';

/**
 * Hook for managing company download state
 *
 * @param {Object} deps - Dependencies
 * @param {Function} deps.getToken - Function to get auth token
 * @param {Object} deps.utilsStore - Utils store with setHeaderMessage
 * @param {Object} deps.profilerAccessStore - Profiler access store with export permissions
 * @param {string} deps.companyId - Company ID
 * @param {string} deps.comparablesId - Comparables ID
 * @param {boolean} deps.isFromFeedbackProject - Whether from feedback project
 * @param {Object} deps.project - Project object
 * @param {(downloadType: string, body: object) => boolean} [deps.onLimitExceeded] -
 *   Called when a download response carries `code: DOWNLOAD_LIMIT_EXCEEDED`. The
 *   host should re-open the bulk-export modal seeded with the BE envelope so
 *   the user sees the precise shortfall instead of a generic error toast. Must
 *   return truthy if it handled the envelope; falsy returns fall back to the
 *   generic error toast (so a host that only handles a subset of download
 *   types can decline the rest).
 * @param {() => void} [deps.onExported] - Called once the BE has answered an export
 *   with the file or with DOWNLOAD_LIMIT_EXCEEDED, i.e. whenever the download
 *   balance is known to have moved or to be spent. The host reconciles the shared
 *   subscription snapshot here so the Downloads counter does not wait for a reload.
 * @returns {Object} Observable state object with download state and actions
 */
const useDownloadState = ({
  getToken,
  utilsStore,
  profilerAccessStore,
  companyId,
  getComparablesId,
  isFromFeedbackProject,
  project,
  onLimitExceeded,
  onExported
}) => {
  const state = useLocalObservable(() => ({
    // ============================================
    // Loading States
    // ============================================
    isDownloadingCompanyOnepager: false,
    setIsDownloadingCompanyOnepager: (value = false) => {
      state.isDownloadingCompanyOnepager = value;
    },

    isDownloadingCompanyFinancials: false,
    setIsDownloadingCompanyFinancials: (value = false) => {
      state.isDownloadingCompanyFinancials = value;
    },

    isDownloadingCompanyFullProfile: false,
    setIsDownloadingCompanyFullProfile: (value = false) => {
      state.isDownloadingCompanyFullProfile = value;
    },

    isDownloadingCompanyComparables: false,
    setIsDownloadingCompanyComparables: (value = false) => {
      state.isDownloadingCompanyComparables = value;
    },

    isDownloadingCompanyContacts: false,
    setIsDownloadingCompanyContacts: (value = false) => {
      state.isDownloadingCompanyContacts = value;
    },

    // ============================================
    // Success States
    // ============================================
    isSuccessDownloadCompanyOnepager: false,
    setSuccessDownloadCompanyOnepager: () => {
      state.isSuccessDownloadCompanyOnepager = true;
    },

    isSuccessDownloadCompanyFinancials: false,
    setSuccessDownloadCompanyFinanials: () => {
      state.isSuccessDownloadCompanyFinancials = true;
    },

    isSuccessDownloadCompanyFullProfile: false,
    setSuccessDownloadCompanyFullProfile: () => {
      state.isSuccessDownloadCompanyFullProfile = true;
    },

    isSuccessDownloadCompanyComparables: false,
    setSuccessDownloadCompanyComparables: () => {
      state.isSuccessDownloadCompanyComparables = true;
    },

    isSuccessDownloadCompanyContacts: false,
    setSuccessDownloadCompanyContacts: () => {
      state.isSuccessDownloadCompanyContacts = true;
    },

    // ============================================
    // Computed: Download Progress
    // ============================================
    get companyDownloadsSuccess() {
      const downloadStates = [
        state.isSuccessDownloadCompanyOnepager,
        state.isSuccessDownloadCompanyFinancials,
        state.isSuccessDownloadCompanyFullProfile,
        state.isSuccessDownloadCompanyComparables,
        state.isSuccessDownloadCompanyContacts
      ];
      const maxDownloads = downloadStates.length;
      const done = downloadStates.filter(Boolean).length;
      return { done, maxDownloads, allDone: done >= maxDownloads };
    },

    // ============================================
    // Download Action Handler
    // ============================================
    onDownloadClick: async (downloadType = '') => {
      if (!downloadType) {
        return;
      }

      const config = DOWNLOAD_CONFIGS[downloadType];
      if (!config) {
        return;
      }

      // Permission and loading state checks
      const permissionChecks = {
        financials: {
          isDownloading: state.isDownloadingCompanyFinancials,
          canExport: profilerAccessStore.canExportProfilerFinancials,
          setLoading: state.setIsDownloadingCompanyFinancials,
          setSuccess: state.setSuccessDownloadCompanyFinanials
        },
        onepager: {
          isDownloading: state.isDownloadingCompanyOnepager,
          canExport: profilerAccessStore.canExportProfilerOnepager,
          setLoading: state.setIsDownloadingCompanyOnepager,
          setSuccess: state.setSuccessDownloadCompanyOnepager
        },
        'full-profile': {
          isDownloading: state.isDownloadingCompanyFullProfile,
          canExport: profilerAccessStore.canExportProfilerFullProfiler,
          setLoading: state.setIsDownloadingCompanyFullProfile,
          setSuccess: state.setSuccessDownloadCompanyFullProfile
        },
        'comparable-companies': {
          isDownloading: state.isDownloadingCompanyComparables,
          canExport: true, // No specific permission check for comparables
          setLoading: state.setIsDownloadingCompanyComparables,
          setSuccess: state.setSuccessDownloadCompanyComparables
        },
        contacts: {
          isDownloading: state.isDownloadingCompanyContacts,
          canExport: profilerAccessStore.canExportCompanyContacts,
          setLoading: state.setIsDownloadingCompanyContacts,
          setSuccess: state.setSuccessDownloadCompanyContacts
        }
      };

      const check = permissionChecks[downloadType];
      if (!check || check.isDownloading || !check.canExport) {
        return;
      }

      check.setLoading(true);

      try {
        const token = await getToken();
        const url = buildDownloadUrl(import.meta.env.VITE_API_BASEURL, downloadType, {
          companyId,
          comparablesId: getComparablesId?.() || null,
          isFromFeedbackProject,
          project
        });

        const result = await downloadProfilerFile({
          url,
          token,
          defaultFilename: config.defaultFilename,
          defaultContentType: config.defaultContentType,
          linkId: config.linkId
        });

        const isLimitExceeded = result.body?.code === DOWNLOAD_LIMIT_EXCEEDED_CODE;
        if (result.success || isLimitExceeded) onExported?.();

        if (result.success) {
          check.setSuccess();
        } else {
          // Give the host a chance to handle a download-limit envelope (e.g.,
          // re-open a bulk-export modal). Falsy return → fall through to the
          // generic error toast below.
          const handled = isLimitExceeded && onLimitExceeded?.(downloadType, result.body);
          if (!handled) {
            console.error(result.error);
            utilsStore.setHeaderMessage(config.errorMessage, HEADER_MESSAGE_TYPES.ERROR);
          }
        }
      } catch (error) {
        console.error(error);
        utilsStore.setHeaderMessage(config.errorMessage, HEADER_MESSAGE_TYPES.ERROR);
      } finally {
        check.setLoading(false);
      }
    }
  }));

  return state;
};

export default useDownloadState;
